import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Download, Zap, AlertTriangle, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Nuova", "Da contattare", "Contatto programmato", "In lavorazione", "Cliente contattato", "Interessato", "Documentazione richiesta", "Pagamento ricevuto", "Attivazione in corso", "Attivato", "Non interessato", "Irreperibile", "Annullato"];

const statusColor = (s) => {
  if (s === "Attivato") return "bg-green-100 text-green-700";
  if (["Non interessato", "Irreperibile", "Annullato"].includes(s)) return "bg-gray-100 text-gray-600";
  if (s === "Nuova") return "bg-blue-100 text-blue-700";
  if (s === "Interessato" || s === "Pagamento ricevuto") return "bg-amber-100 text-amber-700";
  return "bg-yellow-50 text-yellow-800";
};

export default function RequestsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [contactMode, setContactMode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (contactMode) params.contact_mode = contactMode;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const r = await api.get("/admin/requests", { params });
    setRows(r.data);
  }, [q, status, contactMode, dateFrom, dateTo]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const deleteRow = async (e, r) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare definitivamente la richiesta ${r.code} (${r.company_name})?`)) return;
    try {
      await api.delete(`/admin/requests/${r.id}`);
      toast.success(`Richiesta ${r.code} eliminata`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante l'eliminazione");
    }
  };

  const exportData = (format) => {
    const params = new URLSearchParams({ format });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (contactMode) params.set("contact_mode", contactMode);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    window.open(`${API}/admin/export?${params.toString()}`, "_blank");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Richieste</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="export-btn" className="rounded-xl bg-[#111111] hover:bg-black text-white"><Download className="w-4 h-4 mr-2" /> Esporta</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportData("csv")} data-testid="export-csv">CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportData("excel")} data-testid="export-excel">Excel (.xlsx)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportData("pdf")} data-testid="export-pdf">PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4 grid md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="search-input" placeholder="Cerca per codice, agente, cliente, telefono…" className="pl-9 h-11 rounded-xl" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-status" className="h-11 rounded-xl"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={contactMode || "all"} onValueChange={(v) => setContactMode(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-contact" className="h-11 rounded-xl"><SelectValue placeholder="Contatto" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i contatti</SelectItem>
            <SelectItem value="immediate">Immediato</SelectItem>
            <SelectItem value="scheduled">Programmato</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input type="date" data-testid="filter-date-from" className="h-11 rounded-xl" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <Input type="date" data-testid="filter-date-to" className="h-11 rounded-xl" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="requests-table">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-200">
                {["Codice", "Data", "Agente", "Ragione sociale", "Telefono", "Contatto", "Appuntamento", "Stato", "Azioni"].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Nessuna richiesta trovata.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`request-row-${r.code}`} onClick={() => navigate(`/admin/richieste/${r.id}`)}
                  className={`border-b border-gray-100 hover:bg-yellow-50/50 cursor-pointer ${r.is_duplicate ? "bg-amber-50/40" : ""}`}>
                  <td className="px-4 py-3 font-mono font-medium whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      {r.priority === "high" && <Zap className="w-3.5 h-3.5 text-red-500" />}
                      {r.is_duplicate && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                      {r.code}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.agent_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium">{r.company_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{r.phone}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.contact_mode === "immediate" ? "Immediato" : "Programmato"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{r.preferred_date ? `${r.preferred_date} ${r.preferred_time_slot || ""}` : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><Badge className={`${statusColor(r.status)} border-0 font-medium`}>{r.status}</Badge></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-gray-400" />
                      {isAdmin && (
                        <button data-testid={`delete-request-${r.code}`} onClick={(e) => deleteRow(e, r)}
                          className="text-gray-300 hover:text-red-500 transition-colors" title="Elimina richiesta">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-sm text-gray-400">{rows.length} richieste</p>
    </div>
  );
}
